import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useRef } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_maternal-wellness-9/artifacts/yuolopml_Screenshot%202026-03-09%20at%2010.37.55%E2%80%AFPM.png";

// Kinetic Typography
function KineticText({ children, className = "" }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const words = children.split(" ");

  return (
    <span ref={ref} className={className}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          className="inline-block mr-[0.25em]"
          initial={{ opacity: 0, y: 50, rotateX: -90 }}
          animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
          transition={{ 
            duration: 0.8, 
            delay: i * 0.1,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          {word}
        </motion.span>
      ))}
    </span>
  );
}

// Particle Field
function ParticleField() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-[#0A2240]/10 rounded-full"
          initial={{ 
            x: `${Math.random() * 100}%`, 
            y: `${Math.random() * 100}%`,
            scale: Math.random() * 0.5 + 0.5
          }}
          animate={{ 
            y: [null, `${Math.random() * 100}%`],
            opacity: [0.1, 0.3, 0.1]
          }}
          transition={{ 
            duration: 15 + Math.random() * 10, 
            repeat: Infinity,
            ease: "linear"
          }}
        />
      ))}
    </div>
  );
}

export default function NorthStarPage() {
  return (
    <div data-testid="north-star-page" className="min-h-screen flex flex-col items-center justify-center bg-white px-6 relative overflow-hidden">
      <ParticleField />
      
      {/* Subtle grid pattern */}
      <motion.div 
        className="absolute inset-0 opacity-[0.02]" 
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #0A2240 1px, transparent 0)", backgroundSize: "50px 50px" }}
      />
      
      <div className="max-w-4xl mx-auto text-center py-32 relative">
        <motion.div 
          className="inline-block mb-20"
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <motion.img 
            src={LOGO_URL} 
            alt="Healthy Birth Initiative" 
            className="h-28 md:h-36 w-auto"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          />
        </motion.div>

        <motion.p 
          className="text-[#E8A33D] font-body text-xs tracking-[0.5em] uppercase mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Our Obsession
        </motion.p>

        <h1
          data-testid="north-star-title"
          className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-[#0A2240] leading-[1] tracking-tight mb-16"
        >
          <KineticText>Every birth should be a</KineticText>{" "}
          <span className="text-[#E8A33D]"><KineticText>Healthy Birth.</KineticText></span>
        </h1>

        <motion.div 
          className="w-32 h-[2px] bg-gradient-to-r from-transparent via-[#E8A33D] to-transparent mx-auto mb-16"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
        />

        <motion.p 
          className="text-slate-400 font-body text-base md:text-lg leading-relaxed max-w-xl mx-auto mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8 }}
        >
          Accelerate the reduction of low birthweight and preventable neonatal deaths by driving country-led action toward nationally defined goals.
        </motion.p>

        <motion.div 
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2 }}
        >
          <Link to="/about" data-testid="north-star-cta-about">
            <motion.button 
              className="inline-flex items-center justify-center gap-2 bg-[#0A2240] text-white px-8 py-4 text-base font-body font-medium rounded-full transition-all duration-300 group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              About the Initiative <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </Link>
          <Link to="/framework" data-testid="north-star-cta-framework">
            <motion.button 
              className="inline-flex items-center justify-center gap-2 border-2 border-[#0A2240]/20 text-[#0A2240] px-8 py-4 text-base font-body font-medium rounded-full transition-all duration-300 hover:bg-[#0A2240] hover:text-white hover:border-[#0A2240] group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Framework for Action <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
